import { forwardRef, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { useI18n } from '@bravemove/i18n/react/core';

export interface CurrencyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'size' | 'type'
> {
  /** Amount in MINOR units (USD cents · LBP whole pounds). */
  readonly value: number;
  readonly onChange: (value: number) => void;
  readonly currency: string;
  /** When provided, a live converted display in this secondary currency renders below the input. */
  readonly secondaryCurrency?: string | undefined;
  /** Multiplier from primary minor → secondary minor (e.g. 1 USD = 90,000 LBP → fxRate = 900). */
  readonly fxRate?: number | undefined;
  readonly locale?: string;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly placeholder?: string;
  readonly invalid?: boolean;
  readonly disabled?: boolean | undefined;
  readonly id?: string;
  readonly ariaLabel?: string;
  readonly className?: string;
}

const NO_SUBUNIT = new Set(['LBP', 'JPY', 'KRW']);

function symbol(currency: string, locale: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

function format(amountMinor: number, currency: string, locale: string): string {
  const noSub = NO_SUBUNIT.has(currency);
  const value = noSub ? amountMinor : amountMinor / 100;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: noSub ? 0 : 2,
    maximumFractionDigits: noSub ? 0 : 2,
  }).format(value);
}

const SIZE = {
  sm: 'h-[34px] text-[13px]',
  md: 'h-10 text-[14.5px]',
  lg: 'h-12 text-[17px]',
} as const;

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    {
      value,
      onChange,
      currency,
      secondaryCurrency,
      fxRate,
      locale = 'en-US',
      size = 'md',
      placeholder,
      invalid,
      disabled,
      id,
      ariaLabel,
      className,
      'aria-label': nativeAriaLabel,
      ...inputProps
    },
    ref,
  ) {
    const { t } = useI18n();
    const noSub = NO_SUBUNIT.has(currency);
    const displayed = (() => {
      if (!Number.isFinite(value) || value === 0) return '';
      return noSub ? String(value) : (value / 100).toFixed(2);
    })();

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/[^\d.]/g, '');
      if (raw === '') {
        onChange(0);
        return;
      }
      const n = parseFloat(raw);
      if (Number.isNaN(n)) return;
      onChange(Math.round(noSub ? n : n * 100));
    };

    const secondary = useSecondary(value, fxRate, secondaryCurrency, locale);

    return (
      <div className={cn('flex flex-col gap-1', className)}>
        <div
          className={cn(
            'flex min-w-0 items-stretch rounded-lg bg-surface border border-ink-3/80 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:text-base',
            'transition-[box-shadow,border-color] duration-(--duration-tap) ease-(--ease-out) motion-reduce:transition-none',
            'focus-within:border-accent-text focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent-text',
            invalid && 'border-danger focus-within:border-danger focus-within:ring-danger/25',
            disabled && 'opacity-45',
            SIZE[size],
          )}
        >
          <span className="inline-flex items-center ps-3 pe-2 text-ink-3 font-mono text-[12.5px]">
            {symbol(currency, locale)}
          </span>
          <input
            {...inputProps}
            ref={ref}
            id={id}
            type="text"
            inputMode={noSub ? 'numeric' : 'decimal'}
            aria-label={
              nativeAriaLabel ??
              ariaLabel ??
              (id
                ? undefined
                : t('common:forms.amount', { currency, defaultValue: `Amount in ${currency}` }))
            }
            aria-invalid={invalid || inputProps['aria-invalid'] || undefined}
            value={displayed}
            placeholder={placeholder ?? (noSub ? '1,500,000' : '15.00')}
            disabled={disabled}
            onChange={handleChange}
            className="flex-1 min-w-0 pe-3 bg-transparent text-ink font-bold tabular-nums placeholder:text-ink-3 focus:outline-none"
          />
          <span className="inline-flex items-center pe-3 ps-2 text-ink-3 font-mono text-[11px] uppercase tracking-wider border-s border-hairline">
            {currency}
          </span>
        </div>
        {secondary !== null ? (
          <span className="text-[11px] text-ink-3 font-mono ps-1">≈ {secondary}</span>
        ) : null}
      </div>
    );
  },
);

function useSecondary(
  value: number,
  fxRate: number | undefined,
  secondaryCurrency: string | undefined,
  locale: string,
): string | null {
  if (!secondaryCurrency || fxRate === undefined) return null;
  if (!Number.isFinite(value) || value === 0) return null;
  const secondaryMinor = Math.round(value * fxRate);
  return format(secondaryMinor, secondaryCurrency, locale);
}
